/**
 * Utility functions for device-related data.
 */

export const parseDeviceId = (deviceId) => {
    if (!deviceId) return { type: 'Unknown', category: 'Unknown' };

    if (deviceId.startsWith('MEWP')) {
        return {
            type: 'MEWP',
            category: 'ME',
            prefix: 'MEWP',
            unitNumber: deviceId.replace('MEWP', '')
        };
    }

    if (deviceId.startsWith('TL_')) {
        return {
            type: 'Tower Light',
            category: 'TL',
            prefix: 'TL',
            unitNumber: deviceId.split('_')[1]
        };
    }

    if (deviceId.startsWith('BA_')) {
        return {
            type: 'Battery Pack',
            category: 'BA',
            prefix: 'BA',
            unitNumber: deviceId.split('_')[1]
        };
    }

    if (deviceId.startsWith('ME_')) {
        const parts = deviceId.split('_');
        const variant = parts[1];
        const variantNames = {
            'DD': 'D Series Diesel',
            'DB': 'D Series Battery',
            'MD': 'M Series Diesel',
            'MB': 'M Series Battery',
            'TD': 'T Series Diesel',
            'TB': 'T Series Battery'
        };

        return {
            type: 'MEWP',
            category: 'ME',
            variant: variantNames[variant] || 'Unknown',
            variantCode: variant,
            series: variant[0],
            powerType: variant[1] === 'D' ? 'Diesel' : 'Battery',
            prefix: `ME_${variant}`,
            unitNumber: parts[2]
        };
    }

    return { type: 'Unknown', category: 'Unknown' };
};

export const SPN_MAPPING = {
    '100': 'Engine Oil Pressure',
    '110': 'Coolant Temp',
    '190': 'Engine Speed',
    '200': 'Engine Fuel Rate',
    '247': 'Engine Working Hours',
    '250': 'Engine Total Fuel Used',
    '271': 'Main Boom Angle Abs (App)',
    '272': 'Main Boom Angle Abs (App 2)',
    '500': 'SOC',
    '510': 'Traction Motor Speed',
    '511': 'Traction Motor Temperature',
    '512': 'Traction Motor Current',
    '513': 'Actual Torque % / Traction Motor Request Speed',
    '600': 'Pump Motor Speed',
    '601': 'Pump Motor Temperature',
    '602': 'Pump Motor Current',
    '603': 'Pump Motor Request Speed',
    '700': 'Control Battery Voltage (12V)',
    '710': 'System Battery Voltage',
    '898': 'Engine Request Speed',
    '972': 'Turret Swing Angle',
    '1271': 'Lower Boom to Turntable Angle',
    '1272': 'Lower Boom Extend Length',
    '1273': 'Turntable Abs Angle Y (App)',
    '2700': 'Main Boom Angle',
    '2702': 'Main Boom Extend Length',
    '2703': 'Jib Angle',
    '2720': 'Cage Angle',
    '2721': 'Chassis Tilt Angle X',
    '2723': 'Chassis Tilt Angle Y',
    '2724': 'Cage Load',
    '2750': 'Hydraulic Oil Temperature',
    '2751': 'Fuel Level',
    '2755': 'Loadchart',
    '2756': 'Cage Swing Angle'
};

// ---------------------------------------------------------------------------
// Compact sensor labels for the Live Data list.
// The label the API sends comes from the `spn_mapping` table, where several
// entries carry the OEM's full descriptive phrase (and sometimes two aliases
// separated by a slash — "Main Boom Angle/ Main boom to turntable angle").
// Those wrap onto two lines in a narrow column and make the list hard to scan,
// so the screen shows a short form. The long label stays available as the
// row's tooltip, and nothing about the stored/ingested name changes.
//
// Keyed by SPN first (stable), then by the normalised long label (so a machine
// whose payload uses snake_case names is shortened too).
// ---------------------------------------------------------------------------
const normalizeSensorLabel = (s) =>
    String(s || '').toLowerCase().replace(/\s*\/\s*/g, '/').replace(/\s+/g, ' ').trim();

export const SHORT_SENSOR_NAMES = {
    // --- by SPN ---
    '100': 'Oil Pressure',
    '110': 'Coolant Temp',
    '190': 'Engine Speed',
    '200': 'Fuel Rate',
    '247': 'Engine Hours',
    '250': 'Total Fuel Used',
    '271': 'Boom Angle Abs',
    '272': 'Boom Angle Abs 2',
    '500': 'Battery SOC',
    '510': 'Traction Speed',
    '511': 'Traction Temp',
    '512': 'Traction Current',
    '513': 'Traction Req Speed',
    '600': 'Pump Speed',
    '601': 'Pump Temp',
    '602': 'Pump Current',
    '603': 'Pump Req Speed',
    '700': 'Control Battery',
    '710': 'System Battery',
    '898': 'Engine Req Speed',
    '972': 'Turret Swing',
    '1271': 'Lower Boom Angle',
    '1272': 'Lower Boom Extend',
    '1273': 'Turntable Angle Y',
    '2700': 'Main Boom Angle',
    '2702': 'Boom Extend',
    '2703': 'Jib Angle',
    '2720': 'Cage Angle',
    '2721': 'Tilt X',
    '2723': 'Tilt Y',
    '2724': 'Cage Load',
    '2750': 'Hydraulic Temp',
    '2751': 'Fuel Level',
    '2755': 'Load Chart',
    '2756': 'Cage Swing',

    // --- by long label / payload key (normalised) ---
    'main boom angle/main boom to turntable angle': 'Main Boom Angle',
    'main_boom_angle': 'Main Boom Angle',
    'actual torque %/traction motor request speed': 'Traction Req Speed',
    'engine working hours': 'Engine Hours',
    'engine_working_hours': 'Engine Hours',
    'engine oil pressure': 'Oil Pressure',
    'engine_oil_pressure': 'Oil Pressure',
    'engine total fuel used': 'Total Fuel Used',
    'engine fuel rate': 'Fuel Rate',
    'engine request speed': 'Engine Req Speed',
    'engine_request_speed': 'Engine Req Speed',
    'coolant temperature': 'Coolant Temp',
    'coolant_temperature': 'Coolant Temp',
    'hydraulic oil temperature': 'Hydraulic Temp',
    'hydraulic_oil_temperature': 'Hydraulic Temp',
    'traction motor request speed': 'Traction Req Speed',
    'traction motor temperature': 'Traction Temp',
    'traction motor current': 'Traction Current',
    'traction motor speed': 'Traction Speed',
    'pump motor request speed': 'Pump Req Speed',
    'pump motor temperature': 'Pump Temp',
    'pump motor current': 'Pump Current',
    'pump motor speed': 'Pump Speed',
    'control battery voltage (12v)': 'Control Battery',
    'control battery voltage (12 v system)': 'Control Battery',
    'control_battery_voltage': 'Control Battery',
    'system battery voltage': 'System Battery',
    'system_battery_voltage': 'System Battery',
    'chassis tilt angle x': 'Tilt X',
    'chassis_tilt_angle_x': 'Tilt X',
    'chassis tilt angle y': 'Tilt Y',
    'chassis_tilt_angle_y': 'Tilt Y',
    'cage swing angle': 'Cage Swing',
    'cage_swing_angle': 'Cage Swing',
    'turret swing angle': 'Turret Swing',
    'turret_swing_angle': 'Turret Swing',
    'main boom extend length': 'Boom Extend',
    'main_boom_extend_length': 'Boom Extend',
    'lower boom to turntable angle': 'Lower Boom Angle',
    'lower boom extend length': 'Lower Boom Extend',
    'turntable abs angle y (app)': 'Turntable Angle Y',
    'main boom angle abs (app)': 'Boom Angle Abs',
    'main boom angle abs (app 2)': 'Boom Angle Abs 2',
    'soc': 'Battery SOC',
    'loadchart': 'Load Chart',

    // --- Sinoboom i-Link operating conditions ---
    // services/sinoboomSync.js normalises i-Link's own keys onto our canonical
    // names, so most rows are already covered above. These are the signals
    // Sinoboom machines carry that the Autopeepal fleet does not.
    'platform_height': 'Platform Height',
    'load_pct': 'Load',
    'current_work_hours': 'Hours This Shift',
    'travel_motor_hours': 'Travel Motor Hrs',
    'lift_motor_hours': 'Lift Motor Hrs',
    'boom_length': 'Boom Length',
    'boom_extend_length_2': 'Boom Extend 2',
    'cage_load_cell_1': 'Cage Load Cell 1',
    'cage_load_cell_2': 'Cage Load Cell 2',
    'cage_load_raw_1': 'Cage Load Raw 1',
    'cage_load_raw_2': 'Cage Load Raw 2',
    'scissor_angle': 'Scissor Angle',
    'spn': 'Fault SPN',
    'fmi': 'Fault FMI',
};

/**
 * One-line label for a sensor row.
 * @param {string|number} id   sensor id (SPN or payload key)
 * @param {string} label       the long label the API sent
 */
export const shortSensorName = (id, label) => {
    const byId = SHORT_SENSOR_NAMES[String(id ?? '')];
    if (byId) return byId;

    const raw = String(label ?? '').trim() || String(id ?? '').trim();
    if (!raw) return '--';

    const byLabel = SHORT_SENSOR_NAMES[normalizeSensorLabel(raw)];
    if (byLabel) return byLabel;

    // Nothing curated: keep the first alias (the half before a slash) and drop
    // a trailing parenthetical, which is where the length usually comes from.
    return raw
        .split('/')[0]
        .replace(/\s*\([^)]*\)\s*$/, '')
        .replace(/\s+/g, ' ')
        .trim() || raw;
};

export const formatParameterName = (paramKey) => {
    // Check if the key is an SPN (numeric)
    if (SPN_MAPPING[paramKey]) {
        return SPN_MAPPING[paramKey];
    }

    return paramKey
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const formatParameterValue = (paramKey, value) => {
    const units = {
        // Standard keys
        'main_boom_angle': '°',
        'jib_angle': '°',
        'chassis_tilt_angle_x': '°',
        'chassis_tilt_angle_y': '°',
        'cage_angle': '°',
        'turret_swing_angle': '°',
        'cage_swing_angle': '°',
        'hydraulic_oil_temperature': '°C',
        'coolant_temperature': '°C',
        'traction_motor_temperature': '°C',
        'pump_motor_temperature': '°C',
        'cage_load': 'kg',
        'engine_speed': 'RPM',
        'traction_motor_speed': 'RPM',
        'pump_motor_speed': 'RPM',
        'engine_oil_pressure': 'bar',
        'soc': '%',
        'actual_torque_percentage': '%',
        'control_battery_voltage': 'V',
        'system_battery_voltage': 'V',
        'traction_motor_current': 'A',
        'pump_motor_current': 'A',
        'engine_fuel_rate': 'L/h',
        'engine_total_fuel_used': 'L',
        'engine_working_hours': 'hrs',
        'main_boom_extend_length': 'm',
        'lower_boom_extend_length': 'm',
        'regulator_level': '%',

        // SPN keys (Aligned with spn_mapping table)
        '2700': 'deg',
        '2702': 'm',
        '2703': 'deg',
        '2720': 'deg',
        '2721': 'deg',
        '2723': 'deg',
        '2724': 'kg',
        '2750': 'degC',
        '2751': '%',
        '2755': 'kg',
        '2756': 'deg',
        '190': 'rpm',
        '513': '%',
        '110': 'degC',
        '100': 'kPa',
        '200': 'L/h',
        '250': 'L',
        '247': 'hours',
        '898': 'rpm',
        '500': '%',
        '510': 'rpm',
        '511': 'degC',
        '512': 'A',
        '600': 'rpm',
        '601': 'degC',
        '602': 'A',
        '603': 'rpm',
        '700': 'V',
        '710': 'V',
        '271': 'deg',
        '272': 'deg',
        '1271': 'deg',
        '1272': 'mm',
        '1273': 'deg',
        '972': 'deg'
    };

    const unit = units[paramKey] || '';
    return `${value}${unit ? ' ' + unit : ''}`;
};

export const getVariantParameters = (variant) => {
    const params = {
        'DD': [
            'main_boom_angle', 'jib_angle', 'chassis_tilt_angle_x', 'chassis_tilt_angle_y',
            'hydraulic_oil_temperature', 'cage_load', 'engine_speed', 'coolant_temperature',
            'engine_oil_pressure', 'engine_working_hours'
        ],
        'DB': [
            'main_boom_angle', 'jib_angle', 'chassis_tilt_angle_x', 'chassis_tilt_angle_y',
            'soc', 'cage_load', 'traction_motor_speed', 'pump_motor_speed',
            'traction_motor_temperature', 'pump_motor_temperature', 'control_battery_voltage',
            'system_battery_voltage', 'traction_motor_current', 'pump_motor_current',
            'traction_motor_request_speed', 'pump_motor_request_speed'
        ],
        'MD': [
            'main_boom_angle', 'main_boom_extend_length', 'jib_angle', 'cage_angle',
            'chassis_tilt_angle_x', 'chassis_tilt_angle_y', 'hydraulic_oil_temperature',
            'cage_load', 'loadchart', 'engine_speed', 'actual_torque_percentage',
            'coolant_temperature', 'engine_oil_pressure', 'engine_fuel_rate',
            'engine_total_fuel_used', 'engine_working_hours', 'engine_request_speed'
        ],
        'MB': [
            'main_boom_angle', 'main_boom_extend_length', 'jib_angle', 'cage_angle',
            'chassis_tilt_angle_x', 'chassis_tilt_angle_y', 'hydraulic_oil_temperature',
            'cage_load', 'loadchart', 'engine_speed', 'actual_torque_percentage',
            'coolant_temperature', 'engine_oil_pressure', 'engine_fuel_rate',
            'engine_total_fuel_used', 'engine_working_hours', 'engine_request_speed'
        ],
        'TD': [
            'main_boom_to_turntable_angle', 'main_boom_angle_abs_app', 'main_boom_extend_length',
            'lower_boom_to_turntable_angle', 'lower_boom_angle_abs_app', 'lower_boom_extend_length',
            'jib_angle', 'cage_angle', 'chassis_tilt_angle_x', 'chassis_tilt_angle_y',
            'turret_swing_angle', 'turntable_abs_angle_y_app', 'cage_swing_angle',
            'hydraulic_oil_temperature', 'cage_load', 'loadchart', 'engine_speed',
            'actual_torque_percentage', 'coolant_temperature', 'engine_oil_pressure',
            'engine_fuel_rate', 'engine_total_fuel_used', 'engine_working_hours',
            'engine_request_speed'
        ],
        'TB': [
            'main_boom_angle', 'main_boom_extend_length', 'jib_angle', 'cage_angle',
            'chassis_tilt_angle_x', 'chassis_tilt_angle_y', 'turret_swing_angle',
            'cage_swing_angle', 'hydraulic_oil_temperature', 'cage_load', 'loadchart',
            'engine_speed', 'actual_torque_percentage', 'coolant_temperature',
            'engine_oil_pressure', 'engine_fuel_rate', 'engine_total_fuel_used',
            'engine_working_hours', 'engine_request_speed'
        ]
    };

    return params[variant] || [];
};
